const React = require('react');
const { createElement: h, useEffect, useMemo, useRef, useState } = React;
const { parseAiInstructionDocument, serializeAiInstructionDocument, KNOWLEDGE_CATEGORIES, DEFAULT_CATEGORY } = require('../lib/aiInstructionDocument');
require('./AiInstructionModal.css');

function CloseIcon() {
  return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
    h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
}

function PlusIcon() {
  return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
    h('line', { x1: 12, y1: 5, x2: 12, y2: 19 }),
    h('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
  );
}

function ChevronIcon(props) {
  var expanded = !!(props && props.expanded);
  return h('svg', {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    className: expanded ? 'ai-instruction-chevron ai-instruction-chevron-expanded' : 'ai-instruction-chevron'
  },
    h('polyline', { points: '6 9 12 15 18 9' })
  );
}

function createKnowledgeItem(index) {
  return {
    id: 'knowledge-' + Date.now().toString(36) + '-' + index,
    title: '',
    category: DEFAULT_CATEGORY,
    context: ''
  };
}

function AiInstructionModal(props) {
  var value = props && props.value !== undefined && props.value !== null ? String(props.value) : '';
  var aiMemory = props && props.aiMemory !== undefined && props.aiMemory !== null ? String(props.aiMemory) : '';
  var onSave = props && props.onSave ? props.onSave : function() {};
  var onClose = props && props.onClose ? props.onClose : function() {};
  var parsed = useMemo(function() {
    return parseAiInstructionDocument(value);
  }, [value]);
  var [general, setGeneral] = useState(parsed.general);
  var [items, setItems] = useState(parsed.items);
  var [collapsedById, setCollapsedById] = useState({});
  var draftRef = useRef({ general: parsed.general, items: parsed.items });

  useEffect(function() {
    setGeneral(parsed.general);
    setItems(parsed.items);
  }, [parsed]);

  useEffect(function() {
    draftRef.current = { general: general, items: items };
  }, [general, items]);

  useEffect(function() {
    setCollapsedById(function(prev) {
      var next = {};
      for (var i = 0; i < items.length; i += 1) {
        var id = items[i].id;
        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          next[id] = prev[id];
        } else {
          next[id] = i !== 0;
        }
      }
      return next;
    });
  }, [items]);

  useEffect(function() {
    function handleKeyDown(event) {
      if (event.key === 'Escape') onClose();
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        onSave(serializeAiInstructionDocument(draftRef.current));
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return function() {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onSave]);

  function handleOverlayClick(event) {
    if (event.target === event.currentTarget) onClose();
  }

  function handleAddItem() {
    var newItem = createKnowledgeItem(items.length);
    setItems(function(prev) {
      return prev.concat([newItem]);
    });
    setCollapsedById(function(prev) {
      return Object.assign({}, prev, { [newItem.id]: false });
    });
  }

  function handleUpdateItem(itemId, field, nextValue) {
    setItems(function(prev) {
      return prev.map(function(item) {
        if (item.id !== itemId) return item;
        return Object.assign({}, item, { [field]: nextValue });
      });
    });
  }

  function handleRemoveItem(itemId) {
    setItems(function(prev) {
      return prev.filter(function(item) {
        return item.id !== itemId;
      });
    });
    setCollapsedById(function(prev) {
      var next = Object.assign({}, prev);
      delete next[itemId];
      return next;
    });
  }

  function handleClear() {
    setGeneral('');
    setItems([]);
    setCollapsedById({});
  }

  function toggleItemCollapsed(itemId) {
    setCollapsedById(function(prev) {
      return Object.assign({}, prev, { [itemId]: !prev[itemId] });
    });
  }

  function summarizeContext(text) {
    var normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return 'No context yet';
    return normalized.length > 90 ? normalized.slice(0, 90) + '...' : normalized;
  }

  return h('div', { className: 'ai-instruction-overlay', onClick: handleOverlayClick },
    h('div', { className: 'ai-instruction-modal' },
      h('div', { className: 'ai-instruction-header' },
        h('div', null,
          h('div', { className: 'ai-instruction-eyebrow' },
            h('span', { className: 'ai-instruction-eyebrow-dot' }),
            'Workflow context'
          ),
          h('div', { className: 'ai-instruction-title' }, 'AI Instruction'),
          h('div', { className: 'ai-instruction-subtitle' }, 'Add workflow-wide guidance and structured knowledge that AI nodes can reuse.')
        ),
        h('button', { className: 'ai-instruction-close', onClick: onClose, title: 'Close' }, h(CloseIcon))
      ),
      h('div', { className: 'ai-instruction-body' },
        h('div', { className: 'ai-instruction-section ai-instruction-panel' },
          h('div', { className: 'ai-instruction-section-titlerow' },
            h('div', { className: 'ai-instruction-section-title' }, 'General Guidance'),
            h('span', { className: 'ai-instruction-pill ai-instruction-pill-green' }, 'All AI nodes')
          ),
          h('div', { className: 'ai-instruction-section-note' }, 'Applied to every AI node in this workflow.'),
          h('textarea', {
            className: 'ai-instruction-textarea ai-instruction-textarea-general',
            value: general,
            placeholder: 'e.g. Keep responses concise and prefer reusable helper functions.',
            onChange: function(event) { setGeneral(event.target.value); },
            autoFocus: true,
            rows: 9,
            spellCheck: false
          }),
          h('div', { className: 'ai-instruction-charcount' }, String(general.length) + ' characters')
        ),
        h('div', { className: 'ai-instruction-section ai-instruction-panel' },
          h('div', { className: 'ai-instruction-knowledge-header' },
            h('div', null,
              h('div', { className: 'ai-instruction-section-title' }, 'Knowledge Items'),
              h('div', { className: 'ai-instruction-section-subtitle' }, 'Titled context blocks for credentials, API docs, business rules, or domain notes.')
            ),
            h('button', { className: 'ai-instruction-add-btn', onClick: handleAddItem, type: 'button' },
              h(PlusIcon),
              h('span', null, 'Add item')
            )
          ),
          items.length
            ? h('div', { className: 'ai-instruction-items-meta' }, String(items.length) + ' item' + (items.length > 1 ? 's' : ''))
            : null,
          items.length
            ? h('div', { className: 'ai-instruction-knowledge-list' },
                items.map(function(item, index) {
                  var isCollapsed = !!collapsedById[item.id];
                  var titlePreview = String(item.title || '').trim() || 'Untitled';
                  return h('div', { key: item.id, className: 'ai-instruction-knowledge-card' },
                    h('div', { className: 'ai-instruction-knowledge-card-header' },
                      h('button', {
                        type: 'button',
                        className: 'ai-instruction-collapse-btn',
                        onClick: function() { toggleItemCollapsed(item.id); },
                        title: isCollapsed ? 'Expand item' : 'Collapse item'
                      },
                        h(ChevronIcon, { expanded: !isCollapsed }),
                        h('span', { className: 'ai-instruction-pill ai-instruction-pill-blue' }, item.category || DEFAULT_CATEGORY),
                        h('span', { className: 'ai-instruction-knowledge-title-preview' }, titlePreview)
                      ),
                      h('button', {
                        className: 'ai-instruction-remove-btn',
                        type: 'button',
                        onClick: function() { handleRemoveItem(item.id); }
                      }, 'Remove')
                    ),
                    isCollapsed
                      ? h('div', { className: 'ai-instruction-knowledge-summary' }, summarizeContext(item.context))
                      : h('div', { className: 'ai-instruction-knowledge-fields' },
                          h('div', { className: 'ai-instruction-field-row' },
                            h('input', {
                              className: 'ai-instruction-input',
                              type: 'text',
                              value: item.title,
                              placeholder: 'Title',
                              onChange: function(event) { handleUpdateItem(item.id, 'title', event.target.value); }
                            }),
                            h('select', {
                              className: 'ai-instruction-select',
                              value: item.category || DEFAULT_CATEGORY,
                              onChange: function(event) { handleUpdateItem(item.id, 'category', event.target.value); }
                            },
                              KNOWLEDGE_CATEGORIES.map(function(cat) {
                                return h('option', { key: cat, value: cat }, cat);
                              })
                            )
                          ),
                          h('textarea', {
                            className: 'ai-instruction-textarea ai-instruction-textarea-compact',
                            value: item.context,
                            placeholder: 'Context to inject into AI instructions',
                            onChange: function(event) { handleUpdateItem(item.id, 'context', event.target.value); },
                            rows: 3,
                            spellCheck: false
                          })
                        )
                  );
                })
              )
            : h('div', { className: 'ai-instruction-empty' }, 'No knowledge items yet. Add one to keep reusable context organized.')
        ),
        h('div', { className: 'ai-instruction-section ai-instruction-panel ai-instruction-memory-panel' },
          h('div', { className: 'ai-instruction-section-title' }, 'AI Memory'),
          h('div', { className: 'ai-instruction-section-note' }, 'Automatically learned from your previous workflows. Read-only.'),
          aiMemory.trim()
            ? h('textarea', {
                className: 'ai-instruction-textarea ai-instruction-textarea-memory',
                value: aiMemory,
                readOnly: true,
                rows: 10,
                spellCheck: false
              })
            : h('div', { className: 'ai-instruction-empty' }, 'No learned memory yet. It will build up as you create workflows.')
        ),
        h('div', { className: 'ai-instruction-hint' }, 'Press Ctrl+Enter to save.')
      ),
      h('div', { className: 'ai-instruction-footer' },
        h('span', { className: 'ai-instruction-footer-status' },
          String(items.length) + ' item' + (items.length === 1 ? '' : 's') + ' · autosaves on Save'
        ),
        h('div', { className: 'ai-instruction-footer-actions' },
          h('button', { className: 'ai-instruction-btn ai-instruction-btn-secondary', onClick: onClose }, 'Cancel'),
        h('button', {
          className: 'ai-instruction-btn ai-instruction-btn-ghost',
          onClick: handleClear
        }, 'Clear'),
        h('button', {
          className: 'ai-instruction-btn ai-instruction-btn-primary',
          onClick: function() { onSave(serializeAiInstructionDocument({ general: general, items: items })); }
        }, 'Save')
        )
      )
    )
  );
}

module.exports = AiInstructionModal;
